import React, { useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './MfaActivityGraph.css';
import { useTheme } from '../../../../context/ThemeContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MfaActivityGraph = ({ data, timeframe, onTimeframeChange, loading, successRate }) => {
  const chartRef = useRef(null);

  const { isDark } = useTheme();

  const chartData = useMemo(() => {
    if (!data?.breakdown || !Array.isArray(data.breakdown)) return { labels: [], datasets: [] };
    
    const labels = data.breakdown.map(item => {
      const date = new Date(item.timestamp);
      if (timeframe === '24h') {
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    });

    const legitimateData = data.breakdown.map(item => item.legitimate_count || 0);
    const suspiciousData = data.breakdown.map(item => item.suspicious_count || 0);

    // Define your threshold limit numbers (fallback to 80 and 20 if missing from API)
    const legThresholdVal = data.legitimate_threshold || 80;
    const suspThresholdVal = data.suspicious_threshold || 20;

    // Create arrays matching the label length filled with the threshold values
    const legitimateThresholdData = new Array(labels.length).fill(legThresholdVal);
    const suspiciousThresholdData = new Array(labels.length).fill(suspThresholdVal);

    return {
      labels,
      datasets: [
        {
          label: 'Legitimate',
          data: legitimateData,
          borderColor: '#10B981',
          backgroundColor: '#10B981',
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          tension: 0.4,
        },
        {
          label: 'Suspicious',
          data: suspiciousData,
          borderColor: '#EF4444',
          backgroundColor: '#EF4444',
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#EF4444',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          tension: 0.4,
        },
        
      ]
    };
  }, [data, timeframe]);

  const totals = useMemo(() => {
    if (!data) return { legitimate: 0, suspicious: 0, successRate: 0 };
    
    const legitimate = data.legitimate_count || 0;
    const suspicious = data.suspicious_count || 0;
    
    return { legitimate, suspicious, successRate };
  }, [data, successRate]);

  const timePeriodDisplay = useMemo(() => {
    switch(timeframe) {
      case '24h': return '24-hour';
      case '7d': return '7-day';
      case '30d': return '30-day';
      default: return '7-day';
    }
  }, [timeframe]);

  const options = useMemo(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      display: false 
    },
    tooltip: {
      backgroundColor:  isDark ? "#1b3157" : "#ffffff",
      titleColor: isDark ? "#E6F1FF" : "#111827",
      titleFont: {
        size: 13,
        weight: '600'
      },
      bodyColor: isDark ? "#8892B0" : "#374151",
      bodyFont: {
        size: 13
      },
      borderColor: isDark ? "#233554" : "#E5E7EB",
      borderWidth: 1,
      padding: 12,
      displayColors: true,
      boxPadding: 4,
      usePointStyle: true,
      callbacks: {
        label: function(context) {
          if (context.dataset.isThreshold) {
            return null; 
          }
          return `${context.dataset.label}: ${context.parsed.y}`;
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false, // Keeps vertical lines hidden
        drawBorder: false
      },
      ticks: {
        color: '#6B7280',
        font: {
          size: 12
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        display: false, // <-- CHANGED HERE: This completely removes the default horizontal backdrop lines
        drawBorder: false
      },
      ticks: {
        color: '#6B7280',
        font: {
          size: 12
        },
        callback: function(value) {
          if (value >= 1000) return (value / 1000).toFixed(1) + 'k';
          return value;
        }
      }
    }
  }
}), [isDark]);

  if (loading) {
    return (
      <div className="mfa-activity-chart loading">
        <div className="chart-header">
          <div className="chart-title-section">
            <h3>AdapID MFA Activity</h3>
            <p>{timePeriodDisplay} authentication timeline</p>
          </div>
        </div>
        <div className="chart-skeleton">Loading chart data...</div>
      </div>
    );
  }

  if (!data || chartData.labels.length === 0) {
    return (
      <div className="mfa-activity-chart empty">
        <div className="chart-header">
          <div className="chart-title-section">
            <h3>AdapID MFA Activity</h3>
            <p>{timePeriodDisplay} authentication timeline</p>
          </div>
        </div>
        <div className="chart-empty-state">No data available</div>
      </div>
    );
  }

  return (
    <div className="mfa-activity-chart">
      <div className="chart-header">
        <div className="chart-title-section">
          <h3>AdapID MFA Activity</h3>
          <p>{timePeriodDisplay} authentication timeline</p>
        </div>
        
        <div className="chart-controls">
          <div className="legend-container">
            <div className="legend-item">
              <span className="legend-dot legitimate"></span>
              <span className="legend-label">Legitimate:</span>
              <span className="legend-value">{totals.legitimate.toLocaleString()}</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot suspicious"></span>
              <span className="legend-label">Suspicious:</span>
              <span className="legend-value">{totals.suspicious.toLocaleString()}</span>
            </div>
          </div>
          
          <div className="success-badge">
            <span className="success-rate">{successRate}%</span>
            <span className="success-label">Success</span>
          </div>
          
          <select 
            className="timeframe-select"
            value={timeframe}
            onChange={(e) => onTimeframeChange(e.target.value)}
          >
            <option value="24h">24h</option>
            <option value="7d">7d</option>
            <option value="30d">30d</option>
          </select>
        </div>
      </div>

      <div className="chart-container" style={{ height: '300px', position: 'relative' }}>
        <Line 
          ref={chartRef}
          data={chartData} 
          options={options} 
        />
      </div>
    </div>
  );
};

export default MfaActivityGraph;